import React, { useState } from "react";
import { ShieldCheck } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const SECTIONS = [
  {
    heading: "Purpose of Data Collection",
    blocks: [
      { p: "RallyPointGo collects only the information required to operate the merit‑tracking system:" },
      { ul: [
        "Teacher account information (name, email)",
        "Student identifiers (name, class, grade level)",
        "Merit and behavior entries created by teachers",
        "Basic technical data (device type, browser version) for security and troubleshooting",
      ] },
      { p: "This information supports daily use of the platform and allows organizations to review records for up to one year after an account becomes inactive. No biometric, financial, or unnecessary personal data is collected." },
    ],
  },
  {
    heading: "How Data Is Used",
    blocks: [
      { p: "Data is used solely to support the platform’s functionality:" },
      { ul: [
        "Displaying student merit history",
        "Supporting teacher workflows",
        "Maintaining account access",
        "Securing the platform",
        "Improving reliability and performance",
      ] },
      { p: "RallyPointGo does not use personal data for advertising, profiling, or automated decision-making." },
    ],
  },
  {
    heading: "Data Sharing",
    blocks: [
      { p: "RallyPointGo does not sell or share personal data with third parties. Data is shared only with:" },
      { ul: [
        "The school or organization that owns the account",
        "Service providers required to operate the platform (hosting, email delivery)",
      ] },
      { p: "Service providers are contractually required to protect the data and may not use it for their own purposes." },
    ],
  },
  {
    heading: "User Rights (Texas TDPSA)",
    blocks: [
      { p: "Texas users have the right to:" },
      { ul: [
        "Access their personal data, including information retained for up to one year after account inactivity",
        "Correct inaccurate information",
        "Delete personal data where allowed by school policy, including data retained during the one‑year retention period. Deletion requests are executed immediately in the active system. Residual copies in encrypted backups are purged on the standard retention schedule.",
        { text: "Opt‑Out of targeted advertising or data sales", sub: [
          "RallyPointGo does not perform these activities, so opt‑out is always honored by default",
        ] },
      ] },
      { p: "Requests can be submitted through the organization’s administrator or RallyPointGo support." },
    ],
  },
  {
    heading: "Data Security",
    blocks: [
      { p: "RallyPointGo uses industry-standard security practices:" },
      { ul: [
        "Encrypted connections (HTTPS)",
        "Secure authentication",
        "Role-based access controls",
        "Regular updates and monitoring",
      ] },
      { p: "The system is designed to minimize risk and protect user information." },
    ],
  },
  {
    heading: "Data Retention",
    blocks: [
      { p: "RallyPointGo retains data only as long as needed to support active accounts, school reporting requirements, and system integrity. Data may be retained for up to one year after an account becomes inactive to allow organizations to export records, review history, or complete administrative tasks." },
      { p: "After the retention period ends, data is removed according to the organization’s policy and system cleanup procedures." },
    ],
  },
  {
    heading: "Children’s Data",
    blocks: [
      { p: "RallyPointGo is used by schools and youth programs, including high‑school students who may create accounts using their own email addresses and add their phone numbers to their profiles. Student contact information is collected only to support communication and account functionality within the platform." },
      { p: "All student data—including names, emails, phone numbers, merit history, and profile details—is handled under the direction of the school or organization that manages the program. RallyPointGo does not use student contact information for advertising, external communication, or any purpose outside the platform." },
      { p: "Student data may be retained for up to one year after an account becomes inactive to allow organizations to export records, review history, or complete administrative tasks. After the retention period, data is removed according to the organization’s policy and system cleanup procedures." },
      { p: "RallyPointGo does not communicate with students outside the platform and does not create accounts directly for minors. All student access is initiated and supervised by the school or organization." },
    ],
  },
  {
    heading: "No Sale of Personal Data",
    blocks: [
      { p: "RallyPointGo does not:" },
      { ul: [
        "Sell personal data",
        "Share data for advertising",
        "Use data for cross‑context behavioral tracking",
      ] },
      { p: "This is a core part of the platform’s design." },
    ],
  },
  {
    heading: "Contact Information",
    blocks: [
      { p: "For privacy questions or requests, users may contact their organization’s administrator or the RallyPointGo support team." },
    ],
  },
];

export function PrivacyBody() {
  return (
    <div className="space-y-5 text-sm leading-relaxed text-slate-600" data-testid="privacy-body">
      {SECTIONS.map((s) => (
        <section key={s.heading}>
          <h4 className="text-base font-semibold text-slate-900">{s.heading}</h4>
          {s.blocks.map((b, bi) => {
            if (b.p) return <p key={bi} className="mt-1.5">{b.p}</p>;
            return (
              <ul key={bi} className="mt-1.5 ml-5 list-disc space-y-1">
                {b.ul.map((item, ii) => (
                  typeof item === "string"
                    ? <li key={ii}>{item}</li>
                    : (
                      <li key={ii}>
                        {item.text}
                        <ul className="mt-1 ml-5 list-[circle] space-y-1">
                          {item.sub.map((sub, si) => <li key={si}>{sub}</li>)}
                        </ul>
                      </li>
                    )
                ))}
              </ul>
            );
          })}
        </section>
      ))}
      <p className="pt-2 text-xs text-slate-400">© 2026 Joseph Johnson. All rights reserved.</p>
    </div>
  );
}

export default function PrivacyDialog() {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="rounded-full" data-testid="privacy-view-btn">
          <ShieldCheck className="mr-2 h-4 w-4" /> Privacy &amp; Data Practices
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="privacy-dialog" className="max-w-2xl">
        <DialogHeader><DialogTitle className="font-heading">RallyPointGo Privacy &amp; Data Practices</DialogTitle></DialogHeader>
        <div className="max-h-[70vh] overflow-y-auto pr-2"><PrivacyBody /></div>
      </DialogContent>
    </Dialog>
  );
}
